import { useCallback, useEffect, useRef, useState } from "react";
import { ROOM } from "@/components/aiot/lab/HotelRoom";
import { FACTORY_SCRIPT } from "@/components/aiot/lab/FactoryFx";

export const BASE_FX = { night: false, lights: false, ac: false, siren: false, smoke: false, gas: false, valve: false, person: false, door: false, card: false, hot: false, bed: false, broken: null };
const PRE = { no_motion_10: { person: true, lights: true }, card_out: { card: true, person: true, lights: true }, time_2300: { lights: true, person: true } };
const HOME_SCRIPT = {
  base: BASE_FX,
  intro: { lights: true },
  outro: { lights: true, ac: true, night: true },
  pre: (s) => ({ ...BASE_FX, night: s.facts.includes("dark"), ac: s.facts.includes("ac_on"), person: s.facts.includes("occupied") && !["motion", "door_open", "card_in"].includes(s.trigger), ...(PRE[s.trigger] || {}) }),
  cause: { motion: { person: true }, no_motion_10: { person: false }, temp_28: { hot: true }, smoke: { smoke: true }, gas: { gas: true }, door_open: { door: true, person: true },
    card_in: { card: true, person: true }, card_out: { card: false, person: false }, time_2300: { night: true, bed: true, person: false } },
  act: { lights_on: { lights: true }, lights_off: { lights: false }, ac_cool: { ac: true }, ac_off: { ac: false }, siren: { siren: true }, gas_valve: { valve: true },
    welcome: { lights: true, ac: true }, eco: { lights: false, ac: false } },
};
const wait = (ms, tok, ref) => new Promise((ok, no) => setTimeout(() => (ref.current === tok ? ok() : no(new Error("stop"))), ms));

export const useScenarioPlayer = (lab) => {
  const S = lab.id === "factory" ? FACTORY_SCRIPT : HOME_SCRIPT;
  const [fx, setFx] = useState(S.base);
  const [cam, setCam] = useState(null);
  const [cap, setCap] = useState(null);
  const [playing, setPlaying] = useState(false);
  const run = useRef(0);
  const room = ROOM[lab.id];

  const one = async (s, r, tok, step) => {
    const start = S.pre(s);
    setFx(start);
    setCam(room.cam[s.trigger] || room.cam.intro);
    setCap({ s, r, phase: 0, step });
    await wait(900, tok, run);
    const c = S.cause[s.trigger] || {};
    const caused = { ...start, ...(typeof c === "function" ? c(start) : c) };
    setFx(caused);
    await wait(1900, tok, run);
    let end = caused;
    (r?.fired || []).forEach((a) => { end = { ...end, ...(S.act[a] || {}) }; });
    if (r?.reason && ["sensor", "actuator"].includes(r.reason[0])) end = { ...end, broken: r.reason[1] };
    setFx(end);
    setCap({ s, r, phase: 1, step });
    await wait(3000, tok, run);
  };

  const play = useCallback(async (list, { intro, outro } = {}) => {
    const tok = ++run.current;
    setPlaying(true);
    try {
      if (intro) { setFx({ ...S.base, ...S.intro }); setCam(room.cam.intro); setCap({ intro: true }); await wait(3200, tok, run); }
      for (let i = 0; i < list.length; i++) await one(list[i][0], list[i][1], tok, i + 1);
      if (outro) { setFx({ ...S.base, ...S.outro }); setCam(room.cam.outro); setCap({ outro: true }); await wait(4500, tok, run); }
      else { setCap(null); setCam(null); setFx(S.base); }
    } catch { /* stopped */ }
    if (run.current === tok) setPlaying(false);
  }, [room, S]); // eslint-disable-line react-hooks/exhaustive-deps

  const stop = useCallback(() => { run.current += 1; setPlaying(false); setCap(null); setCam(null); setFx(S.base); }, [S]);
  useEffect(() => () => { run.current += 1; }, []);
  return { fx, cam, cap, playing, play, stop };
};
